import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, CalendarRange, Wallet, Moon } from "lucide-react";
import OrgAuthShell, { BoutonPrincipal } from "./OrgAuthShell";

const API = "https://api.komorotel.com/api";

const jour = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

/* Page d'arrivée du lien reçu dans le mail de mission.

   Le jeton identifie la mission ET la personne : le posséder prouve la
   possession de l'adresse. Le voyageur n'a donc ni compte à créer ni email
   à reconfirmer. On lui rappelle son enveloppe, puis on l'envoie sur une
   recherche déjà filtrée sur les bonnes dates.

   Le jeton est gardé dans sessionStorage, pas dans l'URL : il traverse la
   recherche et le checkout sans traîner dans l'historique du navigateur
   ni dans un lien que la personne pourrait partager sans y penser. */
export default function MissionLanding() {
  const { token } = useParams();
  const navigate  = useNavigate();

  const [etat, setEtat]     = useState("chargement");
  const [data, setData]     = useState(null);
  const [erreur, setErreur] = useState("");

  useEffect(() => {
    axios.get(`${API}/organization/mission/${token}`)
      .then(r => {
        setData(r.data);
        setEtat(r.data.canBook ? "pret" : "ferme");
        if (r.data.canBook) {
          try {
            sessionStorage.setItem("komo-mission", JSON.stringify({
              token,
              missionId: r.data.mission._id,
              title: r.data.mission.title,
              organizationName: r.data.organization.name,
              maxPricePerNight: r.data.mission.maxPricePerNight,
              maxNights: r.data.mission.maxNights,
              startDate: r.data.mission.startDate,
              endDate: r.data.mission.endDate,
              traveller: r.data.traveller,
            }));
          } catch {
            // navigation privée : la réservation reste possible, le checkout
            // demandera simplement les informations du voyageur
          }
        }
      })
      .catch(err => {
        setErreur(err.response?.data?.message || "Ce lien n'est plus valable.");
        setEtat("erreur");
      });
  }, [token]);

  const chercher = () => {
    const p = new URLSearchParams();
    if (data.mission.startDate) p.set("checkIn", String(data.mission.startDate).slice(0, 10));
    if (data.mission.endDate)   p.set("checkOut", String(data.mission.endDate).slice(0, 10));
    const lieu = data.mission.city || data.mission.island;
    if (lieu) p.set("destination", lieu);
    navigate(`/hotels?${p.toString()}`);
  };

  if (etat === "chargement") {
    return (
      <div className="org-shell min-h-screen flex items-center justify-center bg-white">
        <div
          className="w-8 h-8 rounded-full animate-spin"
          style={{ border: "3px solid #e5e7eb", borderTopColor: "var(--k-green)" }}
        />
      </div>
    );
  }

  if (etat === "erreur" || etat === "ferme") {
    return (
      <OrgAuthShell
        titre={etat === "ferme" ? "Mission clôturée" : "Lien inutilisable"}
        sousTitre={
          etat === "ferme"
            ? "Cette mission est terminée, ou le compte de votre organisation n'est plus actif. Rapprochez-vous de votre responsable."
            : erreur
        }
        pied={
          <a href="/" className="font-semibold text-[var(--k-green)] hover:underline">
            Aller sur Komorotel
          </a>
        }
      >
        <span />
      </OrgAuthShell>
    );
  }

  const m = data.mission;
  const lignes = [
    { icone: CalendarRange, t: `Du ${jour(m.startDate)} au ${jour(m.endDate)}` },
    (m.city || m.island) ? { icone: MapPin, t: `À ${m.city || m.island}` } : null,
    m.maxPricePerNight ? { icone: Wallet, t: `Jusqu'à ${m.maxPricePerNight} € la nuit` } : null,
    m.maxNights ? { icone: Moon, t: `${m.maxNights} nuits au maximum` } : null,
  ].filter(Boolean);

  return (
    <OrgAuthShell
      titre={`Bonjour ${data.traveller.firstName || ""}`.trim()}
      sousTitre={`${data.organization.name} vous envoie en mission : ${m.title}. Choisissez votre hébergement, c'est l'organisation qui règle.`}
      pied="Dans ce cadre, votre réservation est confirmée tout de suite. En dehors, elle est soumise à votre responsable avant d'être acquise."
    >
      <div className="flex flex-col gap-4">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">
          Ce qui est pris en charge
        </p>

        <div className="flex flex-col gap-2.5">
          {lignes.map(l => {
            const Icone = l.icone;
            return (
              <div key={l.t} className="flex items-center gap-3">
                <Icone size={16} strokeWidth={1.75} className="text-gray-400 shrink-0" />
                <span className="text-[14px] text-gray-900">{l.t}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-4">
          <BoutonPrincipal type="button" onClick={chercher}>
            Choisir mon hébergement
          </BoutonPrincipal>
        </div>
      </div>
    </OrgAuthShell>
  );
}
